import { showDateOnly } from "../../Common/Utility";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export const EXPORT_HEADERS = {
  id: "ID",
  studentName: "Schüler/in",
  address: "Adresse",
  zip: "PLZ",
  ort: "Ort",
  regionId: "Ort (Region)",
  branchId: "Abrechnungstelle",
  tel: "Telefon",
  birthDate: "Geburtsdatum",
  butNumber: "BuT-Nummer",
  regionCenter: "RegionHannover",
  isPartner: "Partner",
};

const formatDate = (date) => {
  return date ? showDateOnly(date) : "";
};

const formatRegionCenter = (value) => {
  return value ? "Ja" : "";
};

export const formatRowData = (row, regionData, branchData) => {
  const regionName =
    regionData.find((region) => region.id === row.regionId)?.regionName || "";
  const branchName =
    branchData.find((branch) => branch.id === row.branchId)?.branchName || "";

  return [
    row.id || "",
    row.studentName || "",
    row.address || "",
    row.zip || "",
    row.ort || "",
    regionName,
    branchName,
    row.tel || "",
    formatDate(row.birthDate),
    row.butNumber || "",
    formatRegionCenter(row.regionCenter),
    row.isPartner,
  ];
};

export const exportToCsv = (rows, regionData, branchData) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";

  const headers = Object.values(EXPORT_HEADERS);
  const csvRows = [headers.join(";")];

  rows.forEach((row) => {
    const rowData = formatRowData(row, regionData, branchData);
    csvRows.push(rowData.join(";"));
  });

  return csvRows.join("\n");
};

export const downloadCsv = (csvString, filename = "Schüler-Liste.csv") => {
  if (!csvString) return;

  const blob = new Blob(["\ufeff" + csvString], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");

  try {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading CSV:", error);
  }
};

export const handleExport = (rows, regionData, branchData, format = "csv") => {
  if (
    !Array.isArray(rows) ||
    !Array.isArray(regionData) ||
    !Array.isArray(branchData)
  ) {
    console.error("Invalid input data for export");
    return;
  }

  try {
    switch (format.toLowerCase()) {
      case "pdf":
        exportToPdf(rows, regionData, branchData);
        break;
      case "csv":
      default:
        const csvString = exportToCsv(rows, regionData, branchData);
        downloadCsv(csvString);
    }
  } catch (error) {
    console.error("Error during export:", error);
  }
};

const calculateColumnWidths = (headers, rows, pageWidth) => {
  if (!headers || !rows) return [];

  // A3 landscape usable width (420mm - margins)
  const availableWidth = pageWidth - 28;

  // Define column width ratios (total should be 100)
  const columnRatios = {
    0: 5, // ID
    1: 15, // Schüler/in
    2: 10, // Adresse
    3: 5, // PLZ
    4: 8, // Ort
    5: 12, // Ort (Region)
    6: 17, // Abrechnungstelle
    7: 8, // Telefon
    8: 8, // Geburtsdatum
    9: 8, // BuT-Nummer
    10: 4, // RegionHannover
    11: 4, // Partner
  };

  return headers.map((_, index) => {
    const ratio = columnRatios[index] || 8;
    return (availableWidth * ratio) / 100;
  });
};

export const exportToPdf = (
  rows,
  regionData,
  branchData,
  title = "Schüler-Liste"
) => {
  if (!Array.isArray(rows) || rows.length === 0) return;

  const headers = Object.values(EXPORT_HEADERS);
  const formattedRows = rows.map((row) =>
    formatRowData(row, regionData, branchData)
  );

  try {
    // Create PDF document in A3 landscape with Unicode support
    const pdf = new jsPDF("l", "mm", "a3");

    // Add Unicode font support
    pdf.setFont("helvetica");
    pdf.setR2L(false);

    const pageWidth = pdf.internal.pageSize.width;

    // Add title
    pdf.setFontSize(16);
    pdf.text(title, 14, 20);

    // Add summary
    pdf.setFontSize(12);
    pdf.text(`Anzahl Schüler: ${rows.length}`, 14, 35);

    // Calculate column widths
    const columnWidths = calculateColumnWidths(
      headers,
      formattedRows,
      pageWidth
    );

    // Use autoTable with optimized settings
    autoTable(pdf, {
      head: [headers],
      body: formattedRows,
      startY: 45,
      theme: "grid",
      columnStyles: headers.reduce((styles, header, index) => {
        styles[index] = {
          cellWidth: columnWidths[index],
          halign: getColumnAlignment(index),
          minCellHeight: 8,
          cellPadding: { left: 2, right: 2, top: 2, bottom: 2 },
          overflow: "linebreak",
          font: "helvetica",
        };
        return styles;
      }, {}),
      styles: {
        fontSize: 8,
        overflow: "linebreak",
        font: "helvetica",
        minCellHeight: 8,
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 9,
        fontStyle: "bold",
        halign: "center",
        minCellHeight: 10,
        valign: "middle",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { top: 45, right: 14, bottom: 20, left: 14 },
      didDrawPage: (data) => {
        // Add page number
        pdf.setFontSize(8);
        pdf.text(
          `Seite ${data.pageNumber} von ${pdf.getNumberOfPages()}`,
          pdf.internal.pageSize.width - 20,
          pdf.internal.pageSize.height - 10,
          { align: "right" }
        );
      },
    });

    // Save the PDF
    pdf.save("Schüler-Liste.pdf");
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

// Helper function to determine column alignment
const getColumnAlignment = (columnIndex) => {
  // Center align specific columns
  if ([0, 3, 8, 9, 10].includes(columnIndex)) return "center";
  // Right align specific columns
  if ([7].includes(columnIndex)) return "right";
  // Left align the rest
  return "left";
};
